/*
 * Moving icons: the storefront's AnimatedIcon (Animate UI 'default' animations,
 * apps/medusa-storefront/src/components/animate-ui/icons) replayed with the Web
 * Animations API. An svg[data-icon] animates while its nearest host is hovered
 * and returns when the pointer leaves, like the storefront. Reduce Motion: none.
 */
(() => {
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const HOST = 'button,a,summary,[role=tab],[data-animate-icon-host],.c-search-field';
  const IO = 'cubic-bezier(.42,0,.58,1)'; // Motion easeInOut
  const EASE = 'cubic-bezier(.25,.1,.35,1)'; // Motion's default tween (0.3s), no-transition returns
  // Motion's default springs for no-transition returns, sampled from motion-dom's
  // spring(): x/y/rotate {stiffness 500, damping 25} settles in 450ms, scale
  // {550, 30} in 250ms. ponytail: starts at rest, ignores the interrupted velocity.
  const spring = (s) => (CSS.supports('transition-timing-function', s) ? s : EASE);
  const SPRING = spring('linear(0,.0873,.2866,.5209,.7385,.9111,1.0289,1.0949,1.1192,1.1146,1.0934,1.0656,1.0384,1.0159,1,.9903,.9861,.9858,.9879,.9911,.9945,.9974,1)');
  const SPRING_SCALE = spring('linear(0,.0961,.3048,.5375,.7426,.8971,1,1.052,1.072,1.0702,1.057,1.04,1)');

  // name: [[child (-1 = the svg), property, target or keyframes, ms, delay, return uses IO]]
  const turn = [[0, 'rotate', '90deg', 400, 0, 1], [1, 'rotate', '90deg', 400, 100, 1]];
  const ICONS = {
    'arrow-right': [[0, 'translate', '3.5px', 300, 0, 1]], // x: 25% of the 14-unit group
    'chevron-down': [[0, 'translate', '0 4px', 300, 0, 1]],
    'chevron-left': [[0, 'translate', '-4px', 300, 0, 1]],
    'chevron-right': [[0, 'translate', '4px', 300, 0, 1]],
    play: [[0, 'translate', '3px', 300, 0, 1]],
    pause: [[0, 'translate', '1.5px', 300], [1, 'translate', '-1.5px', 300]],
    plus: turn,
    x: turn,
    heart: [[-1, 'scale', ['1', '.9', '1.2', '1'], 600]],
    search: [[-1, 'rotate', ['0deg', '17deg', '-10deg', '5deg', '-1deg', '0deg'], 800]],
    user: [[0, 'translate', ['0 0', '0 2px', '0 -2px', '0 0'], 600], [1, 'translate', ['0 0', '0 4px', '0 -2px', '0 0'], 600]],
    check: [[0, 'strokeDasharray', ['0 1', '1 1'], 600], [0, 'opacity', ['0', '1'], 600], [0, 'scale', ['1', '1.1', '1'], 600]],
  };
  // Line attributes (x1/x2) are not CSS: tweened per frame on WAAPI's eased progress.
  const SLIDERS = [{ x2: 4 }, { x1: 8, x2: 8 }, { x1: 8 }, { x2: 16 }, { x1: 16, x2: 16 }, { x1: 20 }, { x2: 7 }, { x1: 11, x2: 11 }, { x1: 11 }];

  // Motion pivots SVG parts on their own box centre; search turns the whole svg on
  // its bottom-right corner (100% 50% where cubica.css already shifts it up by half).
  document.head.append(Object.assign(document.createElement('style'), {
    textContent: 'svg[data-icon]>*{transform-box:fill-box;transform-origin:center}svg[data-icon=search]{transform-origin:100% 100%}:not(button).c-search-field>svg[data-icon]{transform-origin:100% 50%}',
  }));

  const live = new WeakMap(); // element -> {property: Animation}
  const now = (el, p) => getComputedStyle(el)[p];
  const play = (el, p, frames, timing) => {
    const runs = live.get(el) || live.set(el, {}).get(el);
    runs[p]?.cancel();
    runs[p] = el.animate(frames, timing);
  };

  const tween = (el, to, duration, easing) => {
    el.$tween?.cancel();
    const from = {};
    for (const k in to) from[k] = +el.getAttribute(k);
    const run = (el.$tween = new Animation(new KeyframeEffect(null, null, { duration, easing, fill: 'both' })));
    run.play();
    const step = () => {
      const t = run.effect.getComputedTiming().progress;
      if (t == null || el.$tween !== run) return;
      for (const k in to) el.setAttribute(k, from[k] + (to[k] - from[k]) * t);
      if (run.playState === 'running') requestAnimationFrame(step);
    };
    step();
  };

  function animate(svg, on) {
    const name = svg.dataset.icon;
    if (name === 'sliders-horizontal') {
      return SLIDERS.forEach((to, i) => {
        const el = svg.children[i];
        if (!el) return;
        el.$rest ||= Object.fromEntries(Object.keys(to).map((k) => [k, +el.getAttribute(k)]));
        on ? tween(el, to, 400, IO) : tween(el, el.$rest, 300, EASE);
      });
    }
    for (const [i, p, to, ms, delay = 0, io] of ICONS[name] || []) {
      const el = i < 0 ? svg : svg.children[i];
      if (!el) continue;
      const run = live.get(el)?.[p];
      if (on) {
        if (Array.isArray(to)) play(el, p, to.map((v) => ({ [p]: v, easing: IO })), { duration: ms });
        else play(el, p, [{ [p]: now(el, p) }, { [p]: to }], { duration: ms, delay, easing: IO, fill: 'both' });
      } else if (!Array.isArray(to) || run?.playState === 'running') {
        // Back to rest from wherever it is, with the variant's own transition or Motion's default.
        const from = now(el, p);
        run?.cancel();
        const rest = Array.isArray(to) ? to.at(-1) : now(el, p);
        if (from === rest) continue;
        const back = io ? [ms, IO] : /scale/.test(p) ? [250, SPRING_SCALE] : /translate|rotate/.test(p) ? [450, SPRING] : [300, EASE];
        play(el, p, [{ [p]: from }, { [p]: rest }], { duration: back[0], easing: back[1], delay: io ? delay : 0, fill: 'backwards' });
      }
    }
  }

  const listen = (type, on) =>
    document.addEventListener(type, (e) => {
      const host = e.target;
      if (!host.matches?.(HOST)) return;
      // A hidden alternate (the hero's play/pause) shows up at rest, as a fresh storefront icon does.
      for (const svg of host.querySelectorAll('svg[data-icon]')) {
        if (svg.closest(HOST) === host && !(on && svg.hasAttribute('hidden'))) animate(svg, on);
      }
    }, true);
  listen('pointerenter', true);
  listen('pointerleave', false);
})();
